import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { alpha, useTheme } from "@mui/material/styles";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";

import { Reddit as RedditIcon } from "@mui/icons-material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BoltIcon from "@mui/icons-material/Bolt";
import InsightsIcon from "@mui/icons-material/Insights";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { HashLink as Link } from "react-router-hash-link";

import {
  getRedditTasks,
  crawlSubreddit,
  killRedditTask,
  deleteRedditTask,
} from "../../services/reddit_services";
import { getAllResultsCount } from "../../services/generic_services";
import RedditSettingsPopupForm from "../RedditSettingsPopupForm";
import KpiTile from "../results/KpiTile";
import renderStatus from "../common/StatusChip";
import getEnv from "../../config";
import { createSocket } from "../../services/socket";

const baseURL = getEnv("REACT_APP_API_URL");
const REDDIT_COLOR = "#FF4500";

const buildKeywordLabel = (keywords, operator) => {
  const kws = (keywords || []).filter(Boolean);
  if (kws.length <= 1) return kws[0] || "";
  if (operator === "OR") return kws.join(" OR ");
  if (operator === "AND_OR" && kws.length === 3) return `${kws[0]} AND (${kws[1]} OR ${kws[2]})`;
  if (operator === "OR_AND" && kws.length === 3) return `${kws[0]} OR (${kws[1]} AND ${kws[2]})`;
  return kws.join(" AND ");
};

const RedditSchema = Yup.object().shape({
  subreddit: Yup.string().required("Required"),
});

const SectionHeader = ({ title, action }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="h6">{title}</Typography>
    {action}
  </Box>
);

const TaskCard = ({ task, onRun, onStop, onSettings, onDelete }) => {
  const theme = useTheme();
  const isRunning = task.state === "RUNNING";
  const createdAt = task.created_at && task.created_at["$date"]
    ? moment(task.created_at["$date"]).format("YYYY-MM-DD HH:mm")
    : "";

  return (
    <Card
      sx={{
        p: 2,
        borderLeft: `3px solid ${REDDIT_COLOR}`,
        "&:hover": { boxShadow: `0 2px 12px ${alpha(REDDIT_COLOR, 0.15)}` },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Link
              to={`/reddit/results#${task.subreddit}`}
              style={{ color: theme.palette.text.primary, textDecoration: "none" }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                r/{task.subreddit}
              </Typography>
            </Link>
            {task.subreddit_id && (
              <Typography variant="caption" color="text.disabled">
                · id {task.subreddit_id}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            {renderStatus(task.state)}
            {task.monitoring && (
              <Chip
                size="small"
                icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                label={`every ${task.monitoring_interval || 1}d`}
                variant="outlined"
              />
            )}
            {task.focused && (
              <Chip
                size="small"
                icon={<BoltIcon sx={{ fontSize: 14 }} />}
                label={`focused ≥ ${task.threshold ?? 0.5}`}
                variant="outlined"
              />
            )}
            {Array.isArray(task.keywords) && task.keywords.length > 0 && (
              <Chip
                size="small"
                icon={<SearchIcon sx={{ fontSize: 14 }} />}
                label={buildKeywordLabel(task.keywords, task.operator)}
                variant="outlined"
              />
            )}
            {task.submission_limit && (
              <Typography variant="caption" color="text.secondary">
                limit {task.submission_limit}
              </Typography>
            )}
            {createdAt && (
              <Typography variant="caption" color="text.disabled">
                · added {createdAt}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Run now">
            <span>
              <IconButton size="small" color="primary" onClick={() => onRun(task)} disabled={isRunning}>
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Stop">
            <span>
              <IconButton size="small" color="primary" onClick={() => onStop(task)} disabled={!isRunning}>
                <StopIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Task settings">
            <IconButton size="small" onClick={() => onSettings(task)}>
              <SettingsIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete source and related results">
            <IconButton size="small" onClick={() => onDelete(task)}>
              <DeleteIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Card>
  );
};

const STATE_FILTERS = [
  { key: "ALL", label: "All", match: () => true },
  { key: "QUEUED", label: "Queued", match: (s) => s === "PENDING" || s === "QUEUED" },
  { key: "RUNNING", label: "Running", match: (s) => s === "RUNNING" },
  { key: "COMPLETED", label: "Completed", match: (s) => s === "SUCCESS" || s === "COMPLETED" },
  { key: "FAILED", label: "Failed", match: (s) => s === "FAILED" || s === "FAILURE" },
  { key: "KILLED", label: "Killed", match: (s) => s === "KILLED" || s === "REVOKED" || s === "CANCELED" },
];

const PAGE_SIZE = 10;

const RedditTaskPage = () => {
  const [redditTasks, setRedditTasks] = useState([]);
  const [activeTask, setActiveTask] = useState();
  const [openPopupForm, setOpenPopupForm] = useState(false);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("ALL");
  const [taskSearch, setTaskSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showKeywords, setShowKeywords] = useState(false);

  const updateTaskParams = (data) => {
    setRedditTasks((prev) =>
      prev.map((task) =>
        task.subreddit === data.subreddit
          ? {
              ...task,
              monitoring: data.monitoring,
              monitoring_interval: data.monitoring_interval,
              focused: data.focused,
              threshold: data.threshold,
              submission_limit: data.submission_limit,
            }
          : task
      )
    );
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tasks, counts] = await Promise.allSettled([
        getRedditTasks(),
        getAllResultsCount(),
      ]);
      if (tasks.status === "fulfilled") setRedditTasks(tasks.value || []);
      if (counts.status === "fulfilled") setSubmissionsCount(counts.value?.submissions || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!baseURL) return;
    const socket = createSocket();
    socket.on("reddit_state_update", (msg) => {
      const { subreddit, state } = msg;
      setRedditTasks((prev) =>
        prev.map((task) => (task.subreddit === subreddit ? { ...task, state } : task))
      );
    });
    return () => socket.disconnect();
  }, []);

  const submitRedditTask = async (values) => {
    try {
      await crawlSubreddit(values);
    } catch (error) {
      console.log("Reddit submit error", error);
    }
    setTimeout(() => fetchAll(), 2000);
  };

  const killTask = async (task) => {
    try {
      await killRedditTask(task);
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTask = async (task) => {
    try {
      await deleteRedditTask(task);
      setTimeout(() => fetchAll(), 2000);
    } catch (error) {
      console.log(error);
    }
  };

  const openSettings = (task) => {
    setActiveTask(null);
    setActiveTask(task);
    setOpenPopupForm(true);
  };

  const kpis = useMemo(() => {
    const total = redditTasks.length;
    const running = redditTasks.filter((t) => t.state === "RUNNING").length;
    const scheduled = redditTasks.filter((t) => t.monitoring).length;
    return { total, running, scheduled };
  }, [redditTasks]);

  const filteredTasks = useMemo(() => {
    const matcher = (STATE_FILTERS.find((f) => f.key === stateFilter) || STATE_FILTERS[0]).match;
    const q = taskSearch.trim().toLowerCase();
    return redditTasks.filter((t) => {
      if (!matcher(t.state)) return false;
      if (q && !(t.subreddit || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [redditTasks, stateFilter, taskSearch]);

  const paginatedTasks = useMemo(
    () => filteredTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredTasks, page]
  );

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/results" underline="hover" color="text.secondary">
          All Results
        </MuiLink>
        <Typography variant="body2" color="text.primary">Reddit</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Reddit Crawling
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor cybersecurity-relevant subreddits for posts and comments
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value="tasks" exclusive size="small">
            <ToggleButton component={RouterLink} to="/reddit/tasks" value="tasks">
              Tasks
            </ToggleButton>
            <ToggleButton component={RouterLink} to="/reddit/results" value="results">
              Results
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Subreddits"
            value={kpis.total}
            unit="tracked"
            accent={REDDIT_COLOR}
            icon={<RedditIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Running"
            value={kpis.running}
            unit="now"
            accent="#4caf50"
            icon={<BoltIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Scheduled"
            value={kpis.scheduled}
            unit="monitoring"
            accent="#ff9800"
            icon={<ScheduleIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Submissions"
            value={submissionsCount}
            unit="collected"
            accent={REDDIT_COLOR}
            icon={<InsightsIcon fontSize="small" />}
            navigateTo="/reddit/results"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Form + Tasks */}
      <Grid container spacing={3}>
        {/* Add new subreddit */}
        <Grid item xs={12} md={5} lg={4}>
          <Card sx={{ p: 2.5 }}>
            <SectionHeader title="Add new subreddit" />
            <FormGroup>
              <Formik
                initialValues={{
                  subreddit: "cybersecurity",
                  keyword: "",
                  keyword2: "",
                  keyword3: "",
                  keyword_count: 1,
                  operator: "AND",
                  threshold: 0.05,
                  monitoring: false,
                  monitoring_interval: 3,
                  focused: true,
                  submission_limit: 10,
                }}
                validationSchema={RedditSchema}
                onSubmit={(values) => submitRedditTask(values)}
              >
                {({ handleChange, handleBlur, handleSubmit, values, setFieldValue, isValid }) => (
                  <Stack spacing={2.5}>
                    <TextField
                      label="Subreddit name"
                      placeholder="cybersecurity"
                      onChange={handleChange("subreddit")}
                      onBlur={handleBlur("subreddit")}
                      value={values.subreddit}
                      size="small"
                      fullWidth
                    />

                    <Box>
                      <Button
                        onClick={() => setShowKeywords((s) => !s)}
                        size="small"
                        startIcon={<TuneIcon fontSize="small" />}
                        endIcon={
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transition: "transform 0.2s",
                              transform: showKeywords ? "rotate(180deg)" : "none",
                            }}
                          />
                        }
                        sx={{ textTransform: "none", color: "text.secondary", px: 0.5 }}
                      >
                        Advanced keyword search
                      </Button>
                      <Collapse in={showKeywords}>
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <TextField
                          label={values.keyword_count > 1 ? "Keyword 1" : "Keyword"}
                          placeholder="ransomware"
                          onChange={handleChange("keyword")}
                          onBlur={handleBlur("keyword")}
                          value={values.keyword}
                          size="small"
                          fullWidth
                          helperText="Leave empty to crawl the subreddit's hot posts"
                        />

                        <FormControl size="small" fullWidth disabled={!values.keyword.trim()}>
                          <InputLabel>Combine</InputLabel>
                          <Select
                            label="Combine"
                            value={values.keyword_count}
                            onChange={(e) => setFieldValue("keyword_count", Number(e.target.value))}
                          >
                            <MenuItem value={1}>1 keyword</MenuItem>
                            <MenuItem value={2}>2 keywords</MenuItem>
                            <MenuItem value={3}>3 keywords</MenuItem>
                          </Select>
                        </FormControl>

                        {values.keyword.trim() && values.keyword_count >= 2 && (
                          <>
                            <FormControl size="small" fullWidth>
                              <InputLabel>
                                {values.keyword_count === 2 ? "Operator" : "Combination"}
                              </InputLabel>
                              <Select
                                label={values.keyword_count === 2 ? "Operator" : "Combination"}
                                value={values.operator}
                                onChange={handleChange("operator")}
                              >
                                {values.keyword_count === 2 ? (
                                  [
                                    <MenuItem key="AND" value="AND">keyword 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> keyword 2</MenuItem>,
                                    <MenuItem key="OR" value="OR">keyword 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> keyword 2</MenuItem>,
                                  ]
                                ) : (
                                  [
                                    <MenuItem key="AND" value="AND">keyword 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> keyword 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> keyword 3</MenuItem>,
                                    <MenuItem key="OR" value="OR">keyword 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> keyword 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> keyword 3</MenuItem>,
                                    <MenuItem key="AND_OR" value="AND_OR">keyword 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> ( keyword 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> keyword 3 )</MenuItem>,
                                    <MenuItem key="OR_AND" value="OR_AND">keyword 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> ( keyword 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> keyword 3 )</MenuItem>,
                                  ]
                                )}
                              </Select>
                            </FormControl>

                            <TextField
                              label="Keyword 2"
                              placeholder="phishing"
                              onChange={handleChange("keyword2")}
                              onBlur={handleBlur("keyword2")}
                              value={values.keyword2}
                              size="small"
                              fullWidth
                            />
                          </>
                        )}

                        {values.keyword.trim() && values.keyword_count === 3 && (
                          <TextField
                            label="Keyword 3"
                            placeholder="malware"
                            onChange={handleChange("keyword3")}
                            onBlur={handleBlur("keyword3")}
                            value={values.keyword3}
                            size="small"
                            fullWidth
                          />
                        )}
                        </Stack>
                      </Collapse>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
                      >
                        SCHEDULE
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 130px",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Recrawl the source in specified intervals">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                onChange={handleChange("monitoring")}
                                value={values.monitoring}
                                checked={values.monitoring}
                                size="small"
                              />
                            }
                            label="Monitoring"
                          />
                        </Tooltip>
                        <Tooltip title="Monitoring interval in days">
                          <TextField
                            onChange={handleChange("monitoring_interval")}
                            value={values.monitoring_interval}
                            type="number"
                            label="Interval (d)"
                            size="small"
                            disabled={!values.monitoring}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
                      >
                        FILTERING
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 130px",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Enables focused crawler — discards low-confidence results from MISP">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                onChange={handleChange("focused")}
                                value={values.focused}
                                checked={values.focused}
                                size="small"
                              />
                            }
                            label="Focused"
                          />
                        </Tooltip>
                        <Tooltip title="Confidence threshold (0–1)">
                          <TextField
                            onChange={handleChange("threshold")}
                            value={values.threshold}
                            type="number"
                            label="Threshold"
                            size="small"
                            disabled={!values.focused}
                            InputProps={{ inputProps: { min: 0, max: 1, step: 0.1 } }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
                      >
                        LIMITS
                      </Typography>
                      <TextField
                        onChange={handleChange("submission_limit")}
                        value={values.submission_limit}
                        type="number"
                        label="Submission limit"
                        size="small"
                        fullWidth
                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                      />
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!isValid}
                      startIcon={<PlayArrowIcon />}
                      fullWidth
                      sx={{ mt: 0.5 }}
                    >
                      Start crawl
                    </Button>
                  </Stack>
                )}
              </Formik>
            </FormGroup>
          </Card>
        </Grid>

        {/* Active tasks */}
        <Grid item xs={12} md={7} lg={8}>
          <SectionHeader title={`Active tasks${kpis.total ? ` (${kpis.total})` : ""}`} />

          {/* Filter bar */}
          {redditTasks.length > 0 && (
            <Card sx={{ p: 1.5, mb: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={stateFilter}
                  onChange={(_, v) => { if (v) { setStateFilter(v); setPage(1); } }}
                  sx={{ flexWrap: "wrap" }}
                >
                  {STATE_FILTERS.map((f) => (
                    <ToggleButton key={f.key} value={f.key}>{f.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  placeholder="Search subreddit…"
                  value={taskSearch}
                  onChange={(e) => { setTaskSearch(e.target.value); setPage(1); }}
                  sx={{ flex: 1, minWidth: 180 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Card>
          )}

          {redditTasks.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No subreddits being tracked yet. Add one on the left to start.
              </Typography>
            </Card>
          ) : filteredTasks.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No tasks match the current filters.
              </Typography>
            </Card>
          ) : (
            <>
              <Stack spacing={1.5}>
                {paginatedTasks.map((task) => (
                  <TaskCard
                    key={task.subreddit_id}
                    task={task}
                    onRun={submitRedditTask}
                    onStop={killTask}
                    onSettings={openSettings}
                    onDelete={deleteTask}
                  />
                ))}
              </Stack>
              {filteredTasks.length > PAGE_SIZE && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={Math.ceil(filteredTasks.length / PAGE_SIZE)}
                    page={page}
                    color="primary"
                    size="small"
                    onChange={(_, p) => setPage(p)}
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {activeTask && (
        <RedditSettingsPopupForm
          key={activeTask.subreddit}
          open={openPopupForm}
          close={() => setOpenPopupForm(false)}
          task={activeTask}
          updateTaskParams={updateTaskParams}
        />
      )}
    </Box>
  );
};

export default RedditTaskPage;
